import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createBrowserRouter, createRoutesFromElements, RouterProvider } from 'react-router';

import { ErrorBoundary } from '@shared';

import { AuthProvider } from '@/app/providers/AuthProvider';
import { getConfig } from '@/config/env';
import { EmbedProvider } from '@/features/embed/EmbedProvider';
import { ThemeProvider } from '@/features/theme/ThemeProvider';
import { UploadProgressProvider } from '@/features/upload/UploadProgressProvider';
import { IntlProviderShell } from '@/i18n/IntlProviderShell';
import { useTranslations } from '@/i18n/client';
import { AppLayout } from '@/layouts/AppLayout';
import { appRouteElements } from '@/routes/route-elements';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A framed app can be hidden for a long time; don't hammer the Portal on
      // every focus change.
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
});

/**
 * Error boundary with localised labels. Kept inside the intl provider so the
 * fallback speaks the user's language; render errors thrown *by* the intl
 * provider itself surface as the browser's default blank page, which is the
 * documented trade-off for having translated recovery text.
 */
function LocalizedErrorBoundary({ children }: { children: ReactNode }) {
  const t = useTranslations('errors.boundary');

  return (
    <ErrorBoundary
      labels={{
        title: t('title'),
        description: t('description'),
        retry: t('retry'),
        goHome: t('goHome'),
      }}
      onError={(error) => {
        if (import.meta.env.DEV) {
          console.error('[M-DENTURE] Unhandled render error', error);
        }
      }}
    >
      {children}
    </ErrorBoundary>
  );
}

export function App() {
  const { basePath } = getConfig();
  const router = createBrowserRouter(
    createRoutesFromElements(
      appRouteElements(
        <UploadProgressProvider>
          <AppLayout />
        </UploadProgressProvider>,
      ),
    ),
    { basename: basePath },
  );

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <IntlProviderShell>
          <LocalizedErrorBoundary>
            <AuthProvider>
              <EmbedProvider>
                <RouterProvider router={router} />
              </EmbedProvider>
            </AuthProvider>
          </LocalizedErrorBoundary>
        </IntlProviderShell>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
